import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Mail, Send, Eye, Save, X } from "lucide-react";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const DigestSettingsCard = () => {
  const [s, setS] = useState(null);
  const [recipientsInput, setRecipientsInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [previewHtml, setPreviewHtml] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/digest/settings");
      setS(data);
      setRecipientsInput((data.recipients || []).join(", "));
    } catch {
      toast.error("Could not load digest settings");
    }
  };

  useEffect(() => {
    load();
  }, []);

  if (!s) return null;

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/digest/settings", {
        enabled: s.enabled,
        recipients: recipientsInput,
        weekday: s.weekday,
        hour: s.hour,
        minute: s.minute,
      });
      setS(data);
      setRecipientsInput((data.recipients || []).join(", "));
      toast.success("Digest settings saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const preview = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/digest/preview");
      setPreviewHtml(data.html);
    } catch {
      toast.error("Preview failed");
    } finally {
      setBusy(false);
    }
  };

  const sendNow = async () => {
    if (!s.recipients?.length) {
      toast.error("Add at least one recipient first");
      return;
    }
    if (!window.confirm(`Send the digest right now to:\n${s.recipients.join(", ")}?`)) return;
    setBusy(true);
    try {
      await api.post("/digest/send-now");
      toast.success("Digest sent ✓");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Send failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="nb-card p-5" data-testid="digest-card">
        <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
          <div className="flex items-center gap-2">
            <Mail size={20} strokeWidth={2.5} className="text-[#228EBF]" />
            <h2 className="font-display text-xl">Weekly digest</h2>
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              data-testid="digest-enable-toggle"
              type="checkbox"
              className="h-5 w-5 accent-[#5FBE7E]"
              checked={!!s.enabled}
              onChange={(e) => setS({ ...s, enabled: e.target.checked })}
            />
            <span className="text-sm font-medium">
              {s.enabled ? "Scheduled" : "Disabled"}
            </span>
          </label>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <label className="md:col-span-2">
            <div className="label-eyebrow mb-1">Recipients (comma-separated)</div>
            <input
              data-testid="digest-recipients-input"
              className="nb-input"
              value={recipientsInput}
              onChange={(e) => setRecipientsInput(e.target.value)}
              placeholder="ops@footwork.com, manager@footwork.com"
            />
          </label>
          <label>
            <div className="label-eyebrow mb-1">Day of week</div>
            <select
              data-testid="digest-weekday-select"
              className="nb-input"
              value={s.weekday}
              onChange={(e) => setS({ ...s, weekday: Number(e.target.value) })}
            >
              {DAYS.map((d, i) => (
                <option key={d} value={i}>
                  {d}
                </option>
              ))}
            </select>
          </label>
          <label>
            <div className="label-eyebrow mb-1">Time (Sydney)</div>
            <div className="flex items-center gap-2">
              <input
                data-testid="digest-hour-input"
                type="number"
                min="0"
                max="23"
                className="nb-input font-mono-num w-20 text-center"
                value={s.hour}
                onChange={(e) => setS({ ...s, hour: Number(e.target.value) })}
              />
              <span>:</span>
              <input
                data-testid="digest-minute-input"
                type="number"
                min="0"
                max="59"
                className="nb-input font-mono-num w-20 text-center"
                value={s.minute}
                onChange={(e) => setS({ ...s, minute: Number(e.target.value) })}
              />
            </div>
          </label>
        </div>

        <div className="label-eyebrow mt-3 text-[#607087]">
          {s.enabled
            ? `Auto-runs every ${DAYS[s.weekday]} at ${String(s.hour).padStart(2, "0")}:${String(s.minute).padStart(2, "0")} Sydney time. Built with last 7 days of data.`
            : "Toggle on to start scheduled delivery. You can still send a one-off below."}
        </div>

        <div className="flex flex-wrap gap-2 mt-4">
          <button
            data-testid="digest-save"
            onClick={save}
            disabled={busy}
            className="nb-btn nb-btn-primary px-4 py-2.5 flex items-center gap-2"
          >
            <Save size={16} strokeWidth={2.5} /> Save settings
          </button>
          <button
            data-testid="digest-preview"
            onClick={preview}
            disabled={busy}
            className="nb-btn bg-white px-4 py-2.5 flex items-center gap-2"
          >
            <Eye size={16} strokeWidth={2.5} /> Preview
          </button>
          <button
            data-testid="digest-send-now"
            onClick={sendNow}
            disabled={busy}
            className="nb-btn nb-btn-success px-4 py-2.5 flex items-center gap-2"
          >
            <Send size={16} strokeWidth={2.5} /> Send now
          </button>
        </div>
      </div>

      {previewHtml && (
        <div
          className="fixed inset-0 bg-black/60 z-[55] overflow-y-auto"
          data-testid="digest-preview-overlay"
          onClick={() => setPreviewHtml(null)}
        >
          <div className="min-h-full flex items-start justify-center p-4">
            <div
              className="nb-card p-4 w-full max-w-3xl my-6"
              onClick={(e) => e.stopPropagation()}
              data-testid="digest-preview-dialog"
            >
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-display text-xl">Digest preview</h3>
                <button
                  data-testid="digest-preview-close"
                  onClick={() => setPreviewHtml(null)}
                  className="nb-btn bg-white p-2"
                >
                  <X size={18} strokeWidth={2.5} />
                </button>
              </div>
              <iframe
                title="digest"
                srcDoc={previewHtml}
                className="w-full rounded-2xl border border-[#001740]/10 bg-white"
                style={{ height: "70vh" }}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default DigestSettingsCard;
